/**
 * EMETTE Motion Layer v5
 * ─────────────────────────────────────────────────────────────
 * WOW-grade cinematic system:
 *   · Clip-path curtain reveals (editorial, story, marquee)
 *   · Text scramble (eyebrows, stats labels)
 *   · Deep 3D perspective entrances (double angles, Z-depth)
 *   · Perpetual floating stats post-entrance
 *   · Multi-layer parallax (hero, editorial, vbanner, story)
 *   · Magnetic CTAs · Custom cursor · Tilt cards
 *   · rootMargin: -15vh on ALL observers (no premature fires)
 * ─────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  const EASE        = 'cubic-bezier(0.16, 1, 0.3, 1)';
  const EASE_SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
  const EASE_SOFT   = 'cubic-bezier(0.25, 1, 0.5, 1)';
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isCoarse       = window.matchMedia('(pointer: coarse)').matches;

  // All observers fire only when element is well inside the viewport
  const ROOT_MARGIN = '0px 0px -14% 0px';

  // Release an element from the legacy [data-em-reveal] system so its
  // inline 3D timing is not clamped/overridden by emette-animations.js.
  function release(el) {
    if (el) el.removeAttribute('data-em-reveal');
  }

  // Wrap each WORD of a node's text in a span for the blur-to-focus
  // reveal, recursing into child elements (e.g. <em>) so styling is kept.
  function wrapHeroWords(node, state) {
    Array.from(node.childNodes).forEach(child => {
      if (child.nodeType === 3) {
        const frag = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach(ch => {
          if (ch === ' ') { frag.appendChild(document.createTextNode(' ')); return; }
          const s = document.createElement('span');
          s.className = 'em-word3d';
          s.textContent = ch;
          s.style.transitionDelay = (state.i * 95) + 'ms';
          state.i++;
          frag.appendChild(s);
        });
        node.replaceChild(frag, child);
      } else if (child.nodeType === 1) {
        wrapHeroWords(child, state);
      }
    });
  }

  // ── 1. Scroll Progress Bar ─────────────────────────────────────────────
  function initScrollProgress() {
    const bar = document.createElement('div');
    bar.setAttribute('aria-hidden', 'true');
    Object.assign(bar.style, {
      position: 'fixed', top: '0', left: '0', height: '1.5px',
      background: '#DCC7B0', zIndex: '10000', width: '0%',
      transformOrigin: 'left', pointerEvents: 'none',
      transition: 'width 80ms linear'
    });
    document.body.appendChild(bar);
    let rafId = null;
    window.addEventListener('scroll', () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const total = document.body.scrollHeight - window.innerHeight;
        bar.style.width = Math.min(100, (window.scrollY / total) * 100) + '%';
        rafId = null;
      });
    }, { passive: true });
  }

  // ── 2. Text Scramble — Matrix decode effect on eyebrows ───────────────
  const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ·—0123456789';
  function scrambleText(el, delay = 0) {
    if (prefersReduced || el._scrambling) return;
    el._scrambling = true;
    const original = el.textContent.trim();
    const totalFrames = 32;
    let frame = 0;
    setTimeout(() => {
      function step() {
        const progress = frame / totalFrames;
        let result = '';
        for (let i = 0; i < original.length; i++) {
          if (original[i] === ' ' || original[i] === '·' || original[i] === ',') {
            result += original[i];
          } else if (i / original.length < progress) {
            result += original[i];
          } else {
            result += SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
          }
        }
        el.textContent = result;
        frame++;
        if (frame <= totalFrames) requestAnimationFrame(step);
        else el.textContent = original;
      }
      requestAnimationFrame(step);
    }, delay);
  }

  // ── 3. Word-by-Word Reveal ─────────────────────────────────────────────
  function splitIntoWords(el) {
    if (el.dataset.emSplit) return;
    el.dataset.emSplit = '1';
    const nodes = Array.from(el.childNodes);
    el.innerHTML = '';
    let wordIdx = 0;
    nodes.forEach(node => {
      if (node.nodeType === 3) {
        const parts = node.textContent.split(/(\s+)/);
        parts.forEach(part => {
          if (/^\s+$/.test(part)) {
            el.appendChild(document.createTextNode(' '));
          } else if (part) {
            const wrap = document.createElement('span');
            wrap.className = 'em-w-wrap';
            const inner = document.createElement('span');
            inner.className = 'em-word';
            inner.style.transitionDelay = (wordIdx * 65 + 120) + 'ms';
            inner.textContent = part;
            wrap.appendChild(inner);
            el.appendChild(wrap);
            wordIdx++;
          }
        });
      } else {
        const wrap = document.createElement('span');
        wrap.className = 'em-w-wrap';
        const inner = document.createElement('span');
        inner.className = 'em-word';
        inner.style.transitionDelay = (wordIdx * 65 + 120) + 'ms';
        inner.appendChild(node.cloneNode(true));
        wrap.appendChild(inner);
        el.appendChild(wrap);
        wordIdx++;
      }
    });
  }

  function initWordReveal() {
    if (prefersReduced) return;
    const targets = document.querySelectorAll([
      '.emette-story__title',
      '.emette-categories__title',
      '.emette-editorial .emette-editorial__title',
      '.emette-vidquote__quote'
    ].join(','));
    targets.forEach(el => {
      release(el);            // take ownership from legacy reveal system
      el.style.opacity = '1'; // parent visible; the word spans handle the reveal
      splitIntoWords(el);
    });
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.querySelectorAll('.em-word').forEach(w => w.classList.add('em-word--in'));
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2, rootMargin: ROOT_MARGIN });
    targets.forEach(el => observer.observe(el));
  }

  // ── 4. Eyebrow Scramble + Slide ────────────────────────────────────────
  function initEyebrowReveal() {
    if (prefersReduced) return;
    const eyebrows = document.querySelectorAll([
      '.emette-story__eyebrow',
      '.emette-categories__eyebrow',
      '.emette-editorial__eyebrow',
      '.emette-vbanner__eyebrow',
      '.emette-vidquote__eyebrow',
      '.emette-values .label-uppercase'
    ].join(','));
    eyebrows.forEach(el => {
      release(el);
      el.style.opacity   = '0';
      el.style.transform = 'translateY(14px)';
      el.style.transition = `opacity 0.55s ease, transform 0.65s ${EASE}`;
    });
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.style.opacity   = '1';
          e.target.style.transform = 'translateY(0)';
          scrambleText(e.target, 180);
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.5, rootMargin: ROOT_MARGIN });
    eyebrows.forEach(el => obs.observe(el));
  }

  // ── 5. Magnetic CTA Buttons ────────────────────────────────────────────
  function initMagnetic() {
    if (isCoarse || prefersReduced) return;
    const btns = document.querySelectorAll('.emette-hero__cta-primary, .emette-vbanner__cta');
    btns.forEach(btn => {
      let isOver = false, rafId = null;
      let currentX = 0, currentY = 0, targetX = 0, targetY = 0;
      const lerp = (a, b, t) => a + (b - a) * t;
      function animate() {
        if (!isOver && Math.abs(currentX) < 0.1 && Math.abs(currentY) < 0.1) {
          btn.style.transform = ''; rafId = null; return;
        }
        currentX = lerp(currentX, targetX, 0.11);
        currentY = lerp(currentY, targetY, 0.11);
        btn.style.transform = `translate(${currentX.toFixed(2)}px, ${currentY.toFixed(2)}px)`;
        rafId = requestAnimationFrame(animate);
      }
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        targetX = (e.clientX - rect.left - rect.width  / 2) * 0.28;
        targetY = (e.clientY - rect.top  - rect.height / 2) * 0.28;
        isOver = true;
        if (!rafId) rafId = requestAnimationFrame(animate);
      });
      btn.addEventListener('mouseleave', () => {
        isOver = false; targetX = 0; targetY = 0;
        if (!rafId) rafId = requestAnimationFrame(animate);
      });
    });
  }

  // ── 6. Category Tile 3D Hover Tilt ────────────────────────────────────
  function initTilt() {
    if (isCoarse || prefersReduced) return;
    const tiles = document.querySelectorAll('.emette-categories__item');
    tiles.forEach(tile => {
      tile.style.transformStyle = 'preserve-3d';
      tile.addEventListener('mousemove', (e) => {
        const rect = tile.getBoundingClientRect();
        const rotY = ((e.clientX - rect.left) / rect.width  - 0.5) * 10;
        const rotX = (0.5 - (e.clientY - rect.top) / rect.height) * 10;
        tile.style.transform  = `perspective(900px) rotateX(${rotX}deg) rotateY(${rotY}deg) scale(1.04)`;
        tile.style.transition = 'transform 60ms ease-out';
      });
      tile.addEventListener('mouseleave', () => {
        tile.style.transform  = 'perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)';
        tile.style.transition = `transform 600ms ${EASE}`;
      });
    });
  }

  // ── 7. Product Card Stagger ────────────────────────────────────────────
  function initCardStagger() {
    if (prefersReduced) return;
    const grids = document.querySelectorAll('.product-grid, .collection__grid');
    grids.forEach(grid => {
      const cards = Array.from(grid.querySelectorAll('.card-wrapper'));
      cards.forEach((card, i) => {
        card.style.opacity   = '0';
        card.style.transform = 'translateY(44px)';
        card.style.transition = `opacity 0.7s ${EASE} ${i * 100}ms, transform 0.7s ${EASE} ${i * 100}ms`;
      });
      const obs = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            cards.forEach(c => { c.style.opacity = '1'; c.style.transform = 'none'; });
            obs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.05, rootMargin: ROOT_MARGIN });
      obs.observe(grid);
    });
  }

  // ── 8. Values Strip — skew + rotateX entrance ─────────────────────────
  function initValuesEntrance() {
    if (prefersReduced) return;
    const items = document.querySelectorAll('.emette-values__item');
    items.forEach((item, i) => {
      release(item);
      item.style.opacity    = '0';
      item.style.transform  = `perspective(700px) rotateX(-18deg) skewX(-4deg) translateY(70px) scale(0.88)`;
      item.style.transition = `opacity 0.8s ease ${i * 180}ms, transform 1.1s ${EASE} ${i * 180}ms`;
      item.style.willChange = 'transform, opacity';
    });
    const section = document.querySelector('.emette-values');
    if (!section) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          items.forEach(item => {
            item.style.opacity   = '1';
            item.style.transform = 'perspective(700px) rotateX(0deg) skewX(0deg) translateY(0) scale(1)';
          });
          setTimeout(() => items.forEach(t => { t.style.willChange = 'auto'; }), 1400);
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: ROOT_MARGIN });
    obs.observe(section);
  }

  // ── 9. Hero — Full Cinematic Entrance Choreography ───────────────────
  // Sequence: veil lifts → scanline sweeps → bgLetter appears → eyebrow
  // → title lines (skew + slide) → desc → actions → badge spring → vert
  function initHeroClipReveal() {
    if (prefersReduced) return;
    const hero = document.querySelector('.emette-hero--video');
    if (hero) {
      const eyebrow  = hero.querySelector('.emette-hero__eyebrow');
      const title    = hero.querySelector('.emette-hero__title');
      const desc     = hero.querySelector('.emette-hero__desc');
      const actions  = hero.querySelector('.emette-hero__actions');
      const badge    = hero.querySelector('.emette-hero__badge');
      const vert     = hero.querySelector('.emette-hero__vert');
      const bgLetter = hero.querySelector('.emette-hero__bg-letter');
      const video    = hero.querySelector('.emette-hero__video');

      // Set initial hidden states
      if (title) {
        title.style.animation = 'none';
        title.style.perspective = '900px';
        const parts = title.innerHTML.split(/<br\s*\/?>/i);
        title.innerHTML = parts
          .map(p => '<span class="em-hero-line3d">' + p.trim() + '</span>')
          .join('');
        const st = { i: 0 };
        title.querySelectorAll('.em-hero-line3d').forEach(line => wrapHeroWords(line, st));
        title.style.opacity = '1';
      }

      [eyebrow, desc, actions].forEach(el => {
        if (!el) return;
        el.style.opacity = '0'; el.style.transform = 'translateY(26px)'; el.style.transition = 'none';
      });

      if (badge) {
        badge.style.opacity   = '0';
        badge.style.transform = 'scale(0.68) translateY(18px)';
        badge.style.transformOrigin = 'bottom left';
        badge.style.transition = 'none';
      }
      if (vert) {
        vert.style.opacity   = '0';
        vert.style.transform = 'translateX(-32px)';
        vert.style.transition = 'none';
      }
      if (bgLetter) {
        bgLetter.style.opacity = '0';
        bgLetter.style.transform = 'translateX(-30px) scale(0.96)';
        bgLetter.style.transition = 'none';
      }
      // Video starts slightly warm/overexposed — normalises as veil lifts
      if (video) {
        video.style.filter     = 'brightness(1.35) saturate(0.55)';
        video.style.transform  = 'scale(1.06)';
        video.style.transition = 'none';
      }

      // Full-screen dark veil
      const veil = document.createElement('div');
      veil.style.cssText = 'position:absolute;inset:0;z-index:20;background:#272320;pointer-events:none;will-change:opacity';
      hero.appendChild(veil);

      requestAnimationFrame(() => requestAnimationFrame(() => {
        // 1. Lift veil + normalise video
        veil.style.transition = 'opacity 1.2s ease';
        veil.style.opacity    = '0';
        if (video) {
          video.style.transition = `filter 2.2s ease, transform 2.5s ${EASE_SOFT}`;
          video.style.filter     = 'brightness(1) saturate(1)';
          video.style.transform  = 'scale(1)';
        }

        // 2. Scanline sweeps hero top → bottom (400ms after veil starts)
        setTimeout(() => {
          const scanline = document.createElement('div');
          scanline.setAttribute('aria-hidden', 'true');
          scanline.style.cssText = [
            'position:absolute', 'left:0', 'right:0', 'top:0', 'height:1px',
            'background:linear-gradient(90deg,transparent 0%,rgba(220,199,176,0.55) 40%,rgba(249,246,243,0.4) 50%,rgba(220,199,176,0.55) 60%,transparent 100%)',
            `z-index:22`, 'pointer-events:none',
            `transition:top 0.85s ${EASE_SOFT}, opacity 0.4s ease 0.7s`
          ].join(';');
          hero.appendChild(scanline);
          requestAnimationFrame(() => requestAnimationFrame(() => {
            scanline.style.top     = '100%';
            scanline.style.opacity = '0';
          }));
          setTimeout(() => scanline.remove(), 1400);
        }, 400);

        // 3. BG letter materialises
        setTimeout(() => {
          if (bgLetter) {
            bgLetter.style.transition = `opacity 2.0s ease, transform 2.4s ${EASE}`;
            bgLetter.style.opacity   = '1';
            bgLetter.style.transform = 'translateX(0) scale(1)';
          }
        }, 550);

        // 4. Eyebrow letter-scramble + slide
        setTimeout(() => {
          if (eyebrow) {
            eyebrow.style.transition = `opacity 0.7s ease, transform 0.9s ${EASE}`;
            eyebrow.style.opacity    = '1';
            eyebrow.style.transform  = 'translateY(0)';
            scrambleText(eyebrow, 50);
          }
        }, 700);

        // 5. Title — characters flip up in 3D, one after another
        if (title) {
          setTimeout(() => {
            title.querySelectorAll('.em-word3d').forEach(c => c.classList.add('em-word3d--in'));
          }, 820);
        }

        // 6. Description
        setTimeout(() => {
          if (desc) {
            desc.style.transition = `opacity 0.9s ease, transform 1.0s ${EASE}`;
            desc.style.opacity    = '1';
            desc.style.transform  = 'translateY(0)';
          }
        }, 1260);

        // 7. Actions
        setTimeout(() => {
          if (actions) {
            actions.style.transition = `opacity 0.8s ease, transform 0.9s ${EASE}`;
            actions.style.opacity    = '1';
            actions.style.transform  = 'translateY(0)';
          }
        }, 1460);

        // 8. Badge — spring scale entrance
        setTimeout(() => {
          if (badge) {
            badge.style.transition = `opacity 0.6s ease, transform 0.9s ${EASE_SPRING}`;
            badge.style.opacity   = '1';
            badge.style.transform = 'scale(1) translateY(0)';
          }
        }, 1680);

        // 9. Vert text — slides in from left
        setTimeout(() => {
          if (vert) {
            vert.style.transition = `opacity 0.8s ease, transform 1.0s ${EASE}`;
            vert.style.opacity   = '1';
            vert.style.transform = 'translateX(0)';
          }
        }, 1900);

        setTimeout(() => veil.remove(), 2200);
      }));
      return;
    }
    const heroImg = document.querySelector('.emette-hero__image');
    if (!heroImg) return;
    heroImg.style.clipPath  = 'inset(0 0 100% 0)';
    heroImg.style.transition = 'none';
    requestAnimationFrame(() => requestAnimationFrame(() => {
      heroImg.style.clipPath  = 'inset(0 0 0% 0)';
      heroImg.style.transition = `clip-path 1.2s ${EASE} 0.2s`;
    }));
  }

  // ── 10. Story Parallax ────────────────────────────────────────────────
  function initStoryParallax() {
    if (prefersReduced) return;
    const storyImg = document.querySelector('.emette-story__image img');
    if (!storyImg) return;
    let rafId = null;
    window.addEventListener('scroll', () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const section = storyImg.closest('.emette-story');
        if (!section) { rafId = null; return; }
        const rect = section.getBoundingClientRect();
        const viewH = window.innerHeight;
        if (rect.bottom < 0 || rect.top > viewH) { rafId = null; return; }
        const progress = (viewH - rect.top) / (viewH + rect.height);
        const offset   = (progress - 0.5) * 110;
        storyImg.style.transform = `translateY(${offset.toFixed(1)}px) scale(1.12)`;
        rafId = null;
      });
    }, { passive: true });
  }

  // ── 11. Story Section — clip-path reveal ──────────────────────────────
  function initStoryEntrance() {
    if (prefersReduced) return;
    const section = document.querySelector('.emette-story');
    if (!section) return;

    const imageWrap = section.querySelector('.emette-story__image');
    const textCol   = section.querySelector('.emette-story__text') || section.querySelector('.emette-story__content');

    if (imageWrap) {
      release(imageWrap);
      imageWrap.style.clipPath  = 'inset(0 100% 0 0)';
      imageWrap.style.transition = `clip-path 1.4s ${EASE} 0.1s`;
    }
    if (textCol) {
      release(textCol);
      textCol.style.opacity   = '0';
      textCol.style.transform = 'translateX(-55px)';
      textCol.style.transition = `opacity 1.0s ease 0.35s, transform 1.2s ${EASE} 0.35s`;
    }

    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          if (imageWrap) imageWrap.style.clipPath  = 'inset(0 0% 0 0)';
          if (textCol)   { textCol.style.opacity = '1'; textCol.style.transform = 'translateX(0)'; }
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.1, rootMargin: ROOT_MARGIN });
    obs.observe(section);
  }

  // ── 12. Editorial Images — Hover Zoom ─────────────────────────────────
  function initEditorialHover() {
    const wraps = document.querySelectorAll('.emette-editorial__img-wrap');
    wraps.forEach(wrap => {
      const img = wrap.querySelector('img');
      if (!img) return;
      img.style.transition = `transform 1000ms ${EASE}`;
      wrap.addEventListener('mouseenter', () => { img.style.transform = 'scale(1.06)'; });
      wrap.addEventListener('mouseleave', () => { img.style.transform = 'scale(1)'; });
    });
  }

  // ── 13. Palette / VBanner Accordion ───────────────────────────────────
  function initPaletteStagger() {
    const strips = document.querySelectorAll('[data-palette-strip]');
    if (!strips.length) return;
    if (isCoarse) {
      strips.forEach(strip => {
        strip.addEventListener('click', (e) => {
          const isActive = strip.classList.contains('is-active');
          strips.forEach(s => s.classList.remove('is-active'));
          if (!isActive) { strip.classList.add('is-active'); e.preventDefault(); }
        });
      });
    }
    strips.forEach(strip => {
      strip.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (!strip.classList.contains('is-active')) {
            e.preventDefault();
            strips.forEach(s => s.classList.remove('is-active'));
            strip.classList.add('is-active');
          }
        }
      });
    });
  }

  // ── 14. Hero — Unified Scroll + Mouse 3D Parallax ────────────────────
  // 4 independent depth layers react to both scroll position and mouse pos.
  // Depth factor: bg-letter (0.04) < video (0.27 scroll) < text (−0.055)
  //               < badge (0.022) < vert (independent Y)
  // Mouse offsets are lerped at 0.055 for a buttery float feel.
  function initHeroMotionSystem() {
    if (prefersReduced) return;
    const hero     = document.querySelector('.emette-hero--video');
    const media    = document.querySelector('.emette-hero__media') || document.querySelector('.emette-hero__image');
    const text     = document.querySelector('.emette-hero__text');
    const bgLetter = document.querySelector('.emette-hero__bg-letter');
    const badge    = document.querySelector('.emette-hero__badge');
    const vert     = document.querySelector('.emette-hero__vert');
    if (!media) return;

    const MAX_SCROLL = window.innerHeight * 1.5;
    let scrollY = 0;
    let rafId   = null;

    function applyTransforms() {
      // Video: slow scroll push only (no mouse sway)
      media.style.transform = `translateY(${(scrollY * 0.27).toFixed(1)}px)`;

      // Text: scroll rise only (no mouse drift)
      if (text) {
        text.style.transform = `translate(-50%, calc(-50% + ${(scrollY * -0.055).toFixed(1)}px))`;
      }

      rafId = null;
    }

    // Scroll only — mouse parallax removed
    window.addEventListener('scroll', () => {
      scrollY = Math.min(window.scrollY, MAX_SCROLL);
      if (!rafId) rafId = requestAnimationFrame(applyTransforms);
    }, { passive: true });

    // Mark badge + vert as entrance-complete after sequence finishes
    setTimeout(() => {
      if (badge) badge._entranceComplete = true;
      if (vert)  vert._entranceComplete  = true;
    }, 2400);
  }

  // ── 15b. Hero Ambient Particles ───────────────────────────────────────
  // 10 tiny sage-tinted motes float upward — editorial atmosphere.
  // Each is 2-4px, varying speed and horizontal drift.
  function initHeroParticles() {
    if (prefersReduced || isCoarse) return;
    const hero = document.querySelector('.emette-hero--video');
    if (!hero) return;

    const count = 10;
    const colors = [
      'rgba(220,199,176,0.22)', 'rgba(220,199,176,0.14)',
      'rgba(249,246,243,0.16)', 'rgba(249,246,243,0.10)',
    ];

    for (let i = 0; i < count; i++) {
      const p = document.createElement('div');
      p.setAttribute('aria-hidden', 'true');
      const size    = 1.5 + Math.random() * 2.5;
      const left    = 5 + Math.random() * 90;
      const delay   = Math.random() * 8;
      const dur     = 9 + Math.random() * 14;
      const drift   = (Math.random() - 0.5) * 60;
      const color   = colors[Math.floor(Math.random() * colors.length)];
      p.style.cssText = [
        'position:absolute',
        `bottom:-6px`,
        `left:${left}%`,
        `width:${size.toFixed(1)}px`,
        `height:${size.toFixed(1)}px`,
        `border-radius:50%`,
        `background:${color}`,
        `pointer-events:none`,
        `z-index:3`,
        `animation:emParticleRise ${dur.toFixed(1)}s linear ${delay.toFixed(1)}s infinite`,
        `--em-drift:${drift.toFixed(1)}px`
      ].join(';');
      hero.appendChild(p);
    }
  }

  // ── 15. Editorial Multi-layer 3D Parallax ─────────────────────────────
  function initEditorialParallax() {
    if (prefersReduced) return;
    const section = document.querySelector('.emette-editorial');
    if (!section) return;

    const tallItem = section.querySelector('.emette-editorial__item--tall');
    const rightTop = section.querySelector('.emette-editorial__col-right .emette-editorial__item:first-child');
    const rightBot = section.querySelector('.emette-editorial__col-right .emette-editorial__item:last-child');
    if (!tallItem) return;

    const panels = [
      { el: tallItem,  depthY: 0.16,  depthX:  0    },
      { el: rightTop,  depthY: -0.12, depthX:  0.04 },
      { el: rightBot,  depthY: 0.25,  depthX: -0.03 },
    ].filter(p => p.el);

    panels.forEach(p => { p.el.style.willChange = 'transform'; });

    let ticking = false;
    function update() {
      const rect  = section.getBoundingClientRect();
      const vh    = window.innerHeight;
      if (rect.bottom < -100 || rect.top > vh + 100) { ticking = false; return; }
      const progress = (vh - rect.top) / (vh + rect.height);
      panels.forEach(({ el, depthY, depthX }) => {
        const y = (progress - 0.5) * 230 * depthY;
        const x = (progress - 0.5) *  60 * depthX;
        el.style.transform = `translateY(${y.toFixed(1)}px) translateX(${x.toFixed(1)}px)`;
      });
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  // ── 16. Story Section Perspective Entrance ────────────────────────────
  function initPerspectiveEntrance() {
    if (prefersReduced || isCoarse) return;
    const targets = document.querySelectorAll('.emette-story');
    targets.forEach(el => {
      el.style.transformOrigin = '50% 0%';
      el.style.transform       = `perspective(1600px) rotateX(6deg) translateY(30px)`;
      el.style.opacity         = '0';
      el.style.transition      = `transform 1.5s ${EASE}, opacity 1.0s ease`;
      el.style.willChange      = 'transform, opacity';
      const obs = new IntersectionObserver(entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.style.transform = 'perspective(1600px) rotateX(0deg) translateY(0)';
            e.target.style.opacity   = '1';
            obs.unobserve(e.target);
          }
        });
      }, { threshold: 0.08, rootMargin: ROOT_MARGIN });
      obs.observe(el);
    });
  }

  // ── 17. Custom Cursor ─────────────────────────────────────────────────
  function initCursorGlow() {
    if (isCoarse || prefersReduced) return;
    const cursor = document.createElement('div');
    cursor.className = 'em-cursor'; cursor.setAttribute('aria-hidden', 'true');
    const dot = document.createElement('div');
    dot.className = 'em-cursor-dot'; dot.setAttribute('aria-hidden', 'true');
    document.body.appendChild(cursor);
    document.body.appendChild(dot);

    let mx = -200, my = -200, cx = -200, cy = -200, rafId = null;
    const lerp = (a, b, t) => a + (b - a) * t;
    function animate() {
      cx = lerp(cx, mx, 0.10); cy = lerp(cy, my, 0.10);
      cursor.style.transform = `translate(${cx.toFixed(2)}px, ${cy.toFixed(2)}px) translate(-50%, -50%)`;
      dot.style.transform    = `translate(${mx.toFixed(2)}px, ${my.toFixed(2)}px) translate(-50%, -50%)`;
      rafId = requestAnimationFrame(animate);
    }
    rafId = requestAnimationFrame(animate);

    document.addEventListener('mousemove', (e) => {
      mx = e.clientX; my = e.clientY;
      cursor.classList.remove('em-cursor--hidden');
    });
    document.addEventListener('mouseleave', () => cursor.classList.add('em-cursor--hidden'));
    document.addEventListener('mouseenter', () => cursor.classList.remove('em-cursor--hidden'));

    document.querySelectorAll('.emette-editorial__img-wrap, .emette-categories__item').forEach(el => {
      el.addEventListener('mouseenter', () => { cursor.classList.add('em-cursor--view'); cursor.classList.remove('em-cursor--hover'); });
      el.addEventListener('mouseleave', () => cursor.classList.remove('em-cursor--view'));
    });
    document.querySelectorAll('a, button').forEach(el => {
      el.addEventListener('mouseenter', () => { if (!cursor.classList.contains('em-cursor--view')) cursor.classList.add('em-cursor--hover'); });
      el.addEventListener('mouseleave', () => cursor.classList.remove('em-cursor--hover'));
    });
  }

  // ── 18. Lookbook Slideshow ────────────────────────────────────────────
  function initLookbook() {
    const section = document.querySelector('[data-lookbook]');
    if (!section) return;
    const stage     = section.querySelector('[data-lookbook-stage]');
    const slides    = Array.from(section.querySelectorAll('[data-lookbook-slide]'));
    const label     = section.querySelector('[data-lookbook-label]');
    const captionEl = section.querySelector('[data-lookbook-caption]');
    const fill      = section.querySelector('[data-lookbook-progress]');
    const dots      = Array.from(section.querySelectorAll('[data-lookbook-dot]'));
    const prevBtn   = section.querySelector('[data-lookbook-prev]');
    const nextBtn   = section.querySelector('[data-lookbook-next]');
    if (!slides.length) return;

    const N = slides.length, DURATION = 5500;
    let current = 0, timer = null;

    function resetProgress() {
      if (!fill) return;
      fill.style.transition = 'none'; fill.style.width = '0%';
      void fill.offsetWidth;
      fill.style.transition = `width ${DURATION}ms linear`; fill.style.width = '100%';
    }

    function goTo(idx) {
      const prev = slides[current]; current = (idx + N) % N; const next = slides[current];
      const prevVideo = prev.querySelector('.emette-lookbook__slide-video');
      if (prevVideo) { prevVideo.pause(); prevVideo.currentTime = 0; }
      prev.classList.remove('is-active');
      const img = next.querySelector('img');
      if (img) { img.style.animation = 'none'; void img.offsetWidth; img.style.animation = ''; }
      next.classList.add('is-active');
      const nextVideo = next.querySelector('.emette-lookbook__slide-video');
      if (nextVideo) nextVideo.play().catch(() => {});
      if (label) label.textContent = String(current + 1).padStart(2, '0') + ' / ' + String(N).padStart(2, '0');
      if (captionEl) {
        captionEl.style.opacity = '0';
        setTimeout(() => { captionEl.textContent = next.dataset.caption || ''; captionEl.style.opacity = '1'; }, 280);
      }
      dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
      resetProgress();
    }

    function startAuto() { clearInterval(timer); if (prefersReduced) return; timer = setInterval(() => goTo(current + 1), DURATION); }
    function pauseAuto() { clearInterval(timer); if (fill) { const w = getComputedStyle(fill).width; fill.style.transition = 'none'; fill.style.width = w; } }

    resetProgress(); startAuto();
    if (prevBtn) prevBtn.addEventListener('click', () => { goTo(current - 1); startAuto(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { goTo(current + 1); startAuto(); });
    dots.forEach((d, i) => d.addEventListener('click', () => { goTo(i); startAuto(); }));
    if (stage) {
      stage.addEventListener('mouseenter', pauseAuto);
      stage.addEventListener('mouseleave', startAuto);
      let touchStartX = 0;
      stage.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) > 44) { goTo(dx < 0 ? current + 1 : current - 1); startAuto(); }
      });
    }
    section.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { goTo(current - 1); startAuto(); }
      if (e.key === 'ArrowRight') { goTo(current + 1); startAuto(); }
    });
  }

  // ── 19. Cinematic Scroll ──────────────────────────────────────────────
  function initCinematic() {
    const section = document.querySelector('[data-cinematic]');
    if (!section) return;
    const panels = section.querySelectorAll('[data-panel]');
    const words  = section.querySelectorAll('[data-word]');
    const dots   = section.querySelectorAll('[data-dot]');
    if (!panels.length) return;
    let currentIdx = 0, ticking = false;

    function setActive(idx) {
      if (idx === currentIdx && idx !== 0) return;
      panels.forEach((p, i) => { p.style.opacity = i === idx ? '1' : '0'; });
      words.forEach((w, i) => {
        w.classList.remove('is-active', 'is-leaving');
        if (i === idx) w.classList.add('is-active');
        else if (i === currentIdx) { w.classList.add('is-leaving'); setTimeout(() => w.classList.remove('is-leaving'), 750); }
      });
      dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      currentIdx = idx;
    }

    if (words[0]) words[0].classList.add('is-active');
    if (dots[0])  dots[0].classList.add('is-active');

    function update() {
      const rect = section.getBoundingClientRect();
      const scrollRange = section.offsetHeight - window.innerHeight;
      const progress = Math.max(0, Math.min(1, -rect.top / scrollRange));
      const idx = Math.min(panels.length - 1, Math.floor(progress * panels.length));
      setActive(idx);
      ticking = false;
    }
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  }

  // ── 20. Number Counter ────────────────────────────────────────────────
  function initCounters() {
    if (prefersReduced) return;
    const items = document.querySelectorAll('[data-counter]');
    if (!items.length) return;
    function animateCount(el) {
      const span = el.querySelector('[data-count]');
      if (!span || span.dataset.done) return;
      span.dataset.done = '1';
      const target = parseInt(span.dataset.count, 10);
      const suffix = span.dataset.suffix || '';
      const duration = 2000;
      const ease = t => 1 - Math.pow(1 - t, 3);
      const start = performance.now();
      function step(now) {
        const t = ease(Math.min(1, (now - start) / duration));
        span.textContent = Math.round(t * target) + suffix;
        if (now - start < duration) requestAnimationFrame(step);
        else { span.textContent = target + suffix; span.classList.add('em-counted'); }
      }
      requestAnimationFrame(step);
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) { animateCount(entry.target); obs.unobserve(entry.target); }
      });
    }, { threshold: 0.2, rootMargin: ROOT_MARGIN });
    items.forEach(el => obs.observe(el));
  }

  // ── 21. SVG Icon Draw ─────────────────────────────────────────────────
  function initIconDraw() {
    if (prefersReduced) return;
    const items = document.querySelectorAll('.emette-values__item');
    if (!items.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('em-drawn'); obs.unobserve(e.target); } });
    }, { threshold: 0.4, rootMargin: ROOT_MARGIN });
    items.forEach((item, i) => {
      item.querySelectorAll('svg path, svg circle, svg rect').forEach((p, j) => {
        p.style.transitionDelay = (i * 140 + j * 90) + 'ms';
      });
      obs.observe(item);
    });
  }

  // ── 22. Header Scroll Shrink ──────────────────────────────────────────
  function initHeaderShrink() {
    const headerGroup = document.querySelector('.shopify-section-group-header-group');
    if (!headerGroup) return;
    window.addEventListener('scroll', () => {
      headerGroup.classList.toggle('site-header--shrunk', window.scrollY > 80);
    }, { passive: true });
  }

  // ── 23. Product Card Spotlight ────────────────────────────────────────
  function initProductSpotlight() {
    if (isCoarse || prefersReduced) return;
    document.querySelectorAll('.card__media').forEach(card => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--em-mx', ((e.clientX - rect.left) / rect.width  * 100).toFixed(1) + '%');
        card.style.setProperty('--em-my', ((e.clientY - rect.top)  / rect.height * 100).toFixed(1) + '%');
      });
    });
  }

  // ══════════════════════════════════════════════════════════════════════
  //  V5: WOW SCROLL ENTRANCE SYSTEM
  // ══════════════════════════════════════════════════════════════════════

  // ── 24. Marquee — Clip-path wipe from left ────────────────────────────
  // The strip tears open from left to right — fast, bold, cinematic.
  function initMarqueeEntrance() {
    if (prefersReduced) return;
    const marquee = document.querySelector('.emette-marquee');
    if (!marquee) return;
    marquee.style.clipPath  = 'inset(0 100% 0 0)';
    marquee.style.transition = `clip-path 1.0s ${EASE} 0.1s`;
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { marquee.style.clipPath = 'inset(0 0% 0 0)'; obs.unobserve(e.target); }
      });
    }, { threshold: 0.5, rootMargin: ROOT_MARGIN });
    obs.observe(marquee);
  }

  // ── 25. Categories — Scale + Skew 3D Depth ───────────────────────────
  // Each tile scales from depth + skews slightly, alternating Y rotation.
  // Cooperates with initTilt() — tilt is applied AFTER entrance completes.
  function initCategoryEntrance() {
    if (prefersReduced) return;
    const tiles = document.querySelectorAll('.emette-categories__item');
    if (!tiles.length) return;

    tiles.forEach((tile, i) => {
      release(tile);
      const dir = i % 2 === 0 ? -1 : 1;
      tile.style.opacity    = '0';
      tile.style.transform  = `perspective(1100px) rotateY(${dir * 32}deg) translateZ(-120px) translateY(80px) scale(0.84)`;
      tile.style.transition = `opacity 1.0s ease ${i * 120}ms, transform 1.4s ${EASE} ${i * 120}ms`;
      tile.style.willChange = 'transform, opacity';
    });

    const grid = document.querySelector('.emette-categories__grid');
    if (!grid) return;

    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          tiles.forEach(tile => {
            tile.style.opacity   = '1';
            tile.style.transform = 'perspective(1100px) rotateY(0deg) translateZ(0) translateY(0) scale(1)';
          });
          setTimeout(() => tiles.forEach(t => { t.style.willChange = 'auto'; }), 1600);
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.1, rootMargin: ROOT_MARGIN });
    obs.observe(grid);
  }

  // ── 26. Editorial — Clip-path Curtain per Panel ───────────────────────
  // Tall panel wipes from top. Right-top wipes from right. Right-bot from left.
  // After reveal: multi-layer parallax takes over (initEditorialParallax).
  function initEditorialEntrance() {
    if (prefersReduced) return;
    const section = document.querySelector('.emette-editorial');
    if (!section) return;

    const tallItem  = section.querySelector('.emette-editorial__item--tall');
    const rightTop  = section.querySelector('.emette-editorial__col-right .emette-editorial__item:first-child');
    const rightBot  = section.querySelector('.emette-editorial__col-right .emette-editorial__item:last-child');

    // Directions: tall from top, rightTop from right, rightBot from left
    const panels = [
      { el: tallItem,  clip: 'inset(100% 0 0 0)', delay: 80  },
      { el: rightTop,  clip: 'inset(0 0 0 100%)', delay: 280 },
      { el: rightBot,  clip: 'inset(0 100% 0 0)', delay: 180 },
    ].filter(p => p.el);

    panels.forEach(({ el, clip, delay }) => {
      release(el);
      el.style.clipPath  = clip;
      el.style.transition = `clip-path 1.4s ${EASE} ${delay}ms`;
    });

    const header = section.querySelector('.emette-editorial__header');
    if (header) {
      release(header);
      header.style.opacity   = '0';
      header.style.transform = 'translateY(28px)';
      header.style.transition = `opacity 0.8s ease, transform 0.9s ${EASE}`;
    }

    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          panels.forEach(({ el }) => { el.style.clipPath = 'inset(0 0 0 0)'; });
          if (header) { header.style.opacity = '1'; header.style.transform = 'translateY(0)'; }
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: ROOT_MARGIN });
    obs.observe(section);
  }

  // ── 27. Stats — Dramatic Perspective Flip + Perpetual Float ──────────
  // Numbers flip in as if physical scoreboard tiles — big rotateX.
  // After entrance: items float gently in a staggered infinite loop.
  function initStatsEntrance() {
    if (prefersReduced) return;
    const items = document.querySelectorAll('.emette-stats__item');
    if (!items.length) return;

    items.forEach((item, i) => {
      item.style.opacity    = '0';
      item.style.transform  = `perspective(450px) rotateX(78deg) translateY(30px) scale(0.8)`;
      item.style.transition = `opacity 0.85s ease ${i * 180}ms, transform 1.2s ${EASE} ${i * 180}ms`;
      item.style.willChange = 'transform, opacity';
    });

    const dividers = document.querySelectorAll('.emette-stats__divider');
    dividers.forEach((d, i) => {
      d.style.opacity    = '0';
      d.style.transition = `opacity 0.7s ease ${i * 180 + 220}ms`;
    });

    const section = document.querySelector('.emette-stats');
    if (!section) return;

    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          items.forEach((item, i) => {
            item.style.opacity   = '1';
            item.style.transform = `perspective(450px) rotateX(0deg) translateY(0) scale(1)`;

            // Trigger perpetual float after entrance completes
            const floatDelay = i * 180 + 1300;
            setTimeout(() => {
              item.style.transition = 'none';
              item.style.willChange = 'auto';
              item.style.transform  = 'none';
              item.classList.add('em-stat-float');
              item.style.animationDelay = (i * 0.6) + 's';
            }, floatDelay);
          });
          dividers.forEach(d => { d.style.opacity = '1'; });
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.2, rootMargin: ROOT_MARGIN });
    obs.observe(section);
  }

  // ── 28. Video Quote — Deep Split 3D ───────────────────────────────────
  // Left panel sweeps in from -Y 3D depth. Right video from +Y depth.
  // Both combined with scale to feel like pages opening.
  function initVidQuoteEntrance() {
    if (prefersReduced) return;
    const section = document.querySelector('.emette-vidquote');
    if (!section) return;

    const panel  = section.querySelector('.emette-vidquote__panel');
    const media  = section.querySelector('.emette-vidquote__media');
    const mark   = section.querySelector('.emette-vidquote__mark');
    const quote  = section.querySelector('.emette-vidquote__quote');
    const body   = section.querySelector('.emette-vidquote__body');
    const footer = section.querySelector('.emette-vidquote__footer');

    if (panel) {
      panel.style.opacity    = '0';
      panel.style.transform  = `perspective(2000px) rotateY(-26deg) translateX(-110px) translateZ(-80px) scale(0.9)`;
      panel.style.transition = `opacity 1.2s ease, transform 1.6s ${EASE}`;
      panel.style.willChange = 'transform, opacity';
    }
    if (media) {
      media.style.opacity    = '0';
      media.style.transform  = `perspective(2000px) rotateY(20deg) translateX(110px) scale(0.92)`;
      media.style.transition = `opacity 1.1s ease 0.15s, transform 1.5s ${EASE} 0.15s`;
      media.style.willChange = 'transform, opacity';
    }

    const textEls = [mark, quote, body, footer].filter(Boolean);
    textEls.forEach((el, i) => {
      el.style.opacity    = '0';
      el.style.transform  = `perspective(700px) rotateX(12deg) translateY(36px)`;
      el.style.transition = `opacity 0.9s ease ${i * 140 + 350}ms, transform 1.1s ${EASE} ${i * 140 + 350}ms`;
    });

    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          if (panel) { panel.style.opacity = '1'; panel.style.transform = 'perspective(2000px) rotateY(0deg) translateX(0) translateZ(0) scale(1)'; }
          if (media) { media.style.opacity = '1'; media.style.transform = 'perspective(2000px) rotateY(0deg) translateX(0) scale(1)'; }
          textEls.forEach(el => { el.style.opacity = '1'; el.style.transform = 'perspective(700px) rotateX(0deg) translateY(0)'; });
          setTimeout(() => {
            if (panel) panel.style.willChange = 'auto';
            if (media) media.style.willChange = 'auto';
          }, 1800);
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: ROOT_MARGIN });
    obs.observe(section);
  }

  // ── 29. Video Banner — Zoom Scale + Perspective Entrance ─────────────
  // Title scales up dramatically from depth, not just slides.
  // Creates a zoom-in-from-distance feel, very editorial/film.
  function initVideoBannerEntrance() {
    if (prefersReduced) return;
    const section = document.querySelector('.emette-vbanner');
    if (!section) return;

    const eyebrow = section.querySelector('.emette-vbanner__eyebrow');
    const title   = section.querySelector('.emette-vbanner__title');
    const cta     = section.querySelector('.emette-vbanner__cta');

    if (eyebrow) {
      eyebrow.style.opacity    = '0';
      eyebrow.style.transform  = 'translateY(20px) scaleX(0.85)';
      eyebrow.style.transformOrigin = 'left center';
      eyebrow.style.transition = `opacity 0.8s ease, transform 1.0s ${EASE}`;
    }
    if (title) {
      title.style.opacity    = '0';
      title.style.transform  = `perspective(1000px) rotateX(16deg) translateY(70px) scale(0.72)`;
      title.style.transformOrigin = '50% 100%';
      title.style.transition = `opacity 1.0s ease 0.18s, transform 1.5s ${EASE} 0.18s`;
    }
    if (cta) {
      cta.style.opacity    = '0';
      cta.style.transform  = `translateY(32px)`;
      cta.style.transition = `opacity 0.8s ease 0.45s, transform 0.9s ${EASE} 0.45s`;
    }

    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          if (eyebrow) { eyebrow.style.opacity = '1'; eyebrow.style.transform = 'translateY(0) scaleX(1)'; scrambleText(eyebrow, 200); }
          if (title)   { title.style.opacity   = '1'; title.style.transform   = 'perspective(1000px) rotateX(0deg) translateY(0) scale(1)'; }
          if (cta)     { cta.style.opacity     = '1'; cta.style.transform     = 'translateY(0)'; }
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.2, rootMargin: ROOT_MARGIN });
    obs.observe(section);
  }

  // ── 30. Video Banner — Scroll Parallax (deeper) ───────────────────────
  function initVideoBannerParallax() {
    if (prefersReduced) return;
    const video = document.querySelector('.emette-vbanner__video');
    if (!video) return;
    video.style.willChange = 'transform';
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const section = video.closest('.emette-vbanner');
        if (!section) { ticking = false; return; }
        const rect = section.getBoundingClientRect();
        const vh   = window.innerHeight;
        if (rect.bottom < 0 || rect.top > vh) { ticking = false; return; }
        const progress = (vh - rect.top) / (vh + rect.height);
        const offset   = (progress - 0.5) * 170;
        video.style.transform = `translateY(${offset.toFixed(1)}px) scale(1.12)`;
        ticking = false;
      });
    }, { passive: true });
  }

  // ── 31. Master Continuous Parallax Engine ─────────────────────────────
  // ONE rAF scroll loop drives depth movement on images across every
  // section. Works on mobile (scroll-only, no mouse gating). Each target
  // moves opposite to its distance from viewport centre × speed.
  // Images get a baseline scale so translate never reveals edges.
  let _parallaxTargets = [];
  let _parallaxBound = false;

  function registerParallax(selector, speed, scale, axis) {
    document.querySelectorAll(selector).forEach(el => {
      // Avoid double-registering on section reloads
      if (el._emParallax) return;
      el._emParallax = { speed, scale: scale || 1, axis: axis || 'y' };
      el.style.willChange = 'transform';
      el.style.transition = 'none';        // rAF drives it; no tween lag
      el.style.backfaceVisibility = 'hidden';
      _parallaxTargets.push(el);
    });
  }

  function updateParallax() {
    const vh = window.innerHeight;
    const mid = vh / 2;
    for (let i = 0; i < _parallaxTargets.length; i++) {
      const el = _parallaxTargets[i];
      const rect = el.getBoundingClientRect();
      if (rect.bottom < -80 || rect.top > vh + 80) continue;
      const center = rect.top + rect.height / 2;
      const delta  = center - mid;            // px from viewport centre
      const cfg    = el._emParallax;
      const move   = (-delta * cfg.speed);
      if (cfg.axis === 'x') {
        el.style.transform = `translateX(${move.toFixed(1)}px) scale(${cfg.scale})`;
      } else {
        el.style.transform = `translateY(${move.toFixed(1)}px) scale(${cfg.scale})`;
      }
    }
  }

  function initScrollParallaxEngine() {
    if (prefersReduced) return;

    // Imagery — the classic "wow" depth scroll
    registerParallax('.emette-categories__img-wrap img', 0.075, 1.18, 'y');
    registerParallax('.card__media img',                 0.06,  1.14, 'y');
    registerParallax('.emette-story__image img',         0.09,  1.16, 'y');
    // Decorative / structural depth
    registerParallax('.emette-values__icon',             0.05,  1,    'y');
    registerParallax('.emette-stats__label',             0.035, 1,    'y');

    if (!_parallaxTargets.length) return;

    updateParallax(); // initial position

    if (!_parallaxBound) {
      _parallaxBound = true;
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { updateParallax(); ticking = false; });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    }
  }

  // ── 32. Sticky Add-to-Cart Bar (product page) ─────────────────────────
  // Shows the sticky bar only once the real add-to-cart button has been
  // scrolled PAST (not merely below the fold), so it never appears early.
  function initStickyATC() {
    const bar = document.querySelector('[data-emette-satc]');
    if (!bar) return;
    const realBtn = document.querySelector('.product__info-container .product-form__submit')
                 || document.querySelector('.product-form__submit');
    if (!realBtn) return;

    const show = () => { bar.classList.add('is-visible'); bar.setAttribute('aria-hidden', 'false'); };
    const hide = () => { bar.classList.remove('is-visible'); bar.setAttribute('aria-hidden', 'true'); };

    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { hide(); }
        else if (e.boundingClientRect.top < 0) { show(); }  // scrolled past it
        else { hide(); }                                     // still below fold
      });
    }, { threshold: 0 });
    obs.observe(realBtn);
  }

  // ── 33. Shop the Look — hotspot tap + staggered entrance ──────────────
  function initShopLook() {
    const sections = document.querySelectorAll('[data-shop-the-look]');
    sections.forEach(section => {
      const hotspots = section.querySelectorAll('[data-stl-hotspot]');
      if (!hotspots.length) return;

      hotspots.forEach(hs => {
        // Flip the card below the dot when the hotspot sits near the top
        const topPct = parseFloat(hs.style.top) || 50;
        if (topPct < 38) hs.setAttribute('data-flip', '');

        const toggle = hs.querySelector('[data-stl-toggle]');
        if (toggle) {
          toggle.addEventListener('click', (e) => {
            e.preventDefault();
            const isOpen = hs.classList.contains('is-open');
            hotspots.forEach(h => h.classList.remove('is-open'));
            if (!isOpen) hs.classList.add('is-open');
          });
        }
      });

      // Tap outside closes any open card
      document.addEventListener('click', (e) => {
        if (!section.contains(e.target)) hotspots.forEach(h => h.classList.remove('is-open'));
      });

      // Staggered pop-in entrance
      if (!prefersReduced) {
        hotspots.forEach((hs, i) => {
          hs.style.opacity   = '0';
          hs.style.transform = 'translate(-50%, -50%) scale(0.4)';
          hs.style.transition = `opacity 0.6s ease ${i * 130 + 250}ms, transform 0.7s ${EASE_SPRING} ${i * 130 + 250}ms`;
        });
        const obs = new IntersectionObserver(entries => {
          entries.forEach(e => {
            if (e.isIntersecting) {
              hotspots.forEach(hs => {
                hs.style.opacity   = '1';
                hs.style.transform = 'translate(-50%, -50%) scale(1)';
              });
              obs.unobserve(e.target);
            }
          });
        }, { threshold: 0.2, rootMargin: ROOT_MARGIN });
        obs.observe(section);
      }
    });
  }

  // ── 34. Promisiunea EMETTE — staggered entrance ───────────────────────
  function initPromiseEntrance() {
    if (prefersReduced) return;
    const section = document.querySelector('[data-promise]');
    if (!section) return;

    const lead    = section.querySelector('[data-promise-lead]');
    const rows    = section.querySelectorAll('[data-promise-row]');
    const eyebrow = section.querySelector('.emette-promise__eyebrow');

    if (lead) {
      lead.style.opacity   = '0';
      lead.style.transform = 'translateY(34px)';
      lead.style.transition = `opacity 1s ease, transform 1.1s ${EASE}`;
    }
    rows.forEach((row, i) => {
      row.style.opacity   = '0';
      row.style.transform = 'translateX(44px)';
      row.style.transition = `opacity 0.8s ease ${i * 130 + 220}ms, transform 1.0s ${EASE} ${i * 130 + 220}ms`;
    });

    const lastDelay = rows.length * 130 + 220;
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          if (lead) { lead.style.opacity = '1'; lead.style.transform = 'translateY(0)'; }
          rows.forEach(r => { r.style.opacity = '1'; r.style.transform = 'translateX(0)'; });
          if (eyebrow) scrambleText(eyebrow, 280);
          // Restore CSS hover transition after entrance settles
          setTimeout(() => {
            rows.forEach(r => { r.style.transition = ''; r.style.transform = ''; });
          }, lastDelay + 1100);
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.15, rootMargin: ROOT_MARGIN });
    obs.observe(section);
  }

  // ── Init ───────────────────────────────────────────────────────────────
  function init() {
    initScrollProgress();
    initHeroClipReveal();
    initHeroMotionSystem();
    // initHeroParticles();  // disabled — 10 infinite animations were competing
                              // with the hero video on the compositor and causing
                              // stutter on lower-end devices/GPUs.
    initWordReveal();
    initEyebrowReveal();
    initMagnetic();
    initCategoryEntrance();   // entrance before tilt — sets initial state
    initTilt();
    initCardStagger();
    initValuesEntrance();
    initStoryEntrance();
    initEditorialHover();
    initEditorialEntrance();  // clip-path per panel
    initEditorialParallax();  // parallax layering
    initPaletteStagger();
    initPerspectiveEntrance();
    initVidQuoteEntrance();
    initVideoBannerEntrance();
    initVideoBannerParallax();
    initMarqueeEntrance();
    initCursorGlow();
    initLookbook();
    initCinematic();
    initCounters();
    initStatsEntrance();
    initIconDraw();
    initHeaderShrink();
    initProductSpotlight();
    initScrollParallaxEngine();  // master continuous depth scroll (mobile too)
    initStickyATC();             // product page sticky add-to-cart
    initShopLook();              // shoppable lookbook hotspots (if present)
    initPromiseEntrance();       // promise band staggered entrance
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    initWordReveal();
    initEyebrowReveal();
    initMagnetic();
    initCategoryEntrance();
    initTilt();
    initCardStagger();
    initValuesEntrance();
    initPaletteStagger();
    initEditorialHover();
    initEditorialEntrance();
    initEditorialParallax();
    initStoryEntrance();
    initPerspectiveEntrance();
    initVidQuoteEntrance();
    initVideoBannerEntrance();
    initVideoBannerParallax();
    initMarqueeEntrance();
    initLookbook();
    initCinematic();
    initCounters();
    initStatsEntrance();
    initIconDraw();
    initProductSpotlight();
    initScrollParallaxEngine();
    initStickyATC();
    initShopLook();
    initPromiseEntrance();
  });

})();
